import React from 'react';
import i18n from 'i18n-js';
import { MomentInput } from 'moment';

import { LegendRules } from '@components/machines/components/timeline/calculations/index';
import { TimelineBlockTitle } from './TimelineBlockTitle';
import { JOBS_CONFIG, TIMELINE_CONFIG, TIMELINE_LINE_CONFIG, TIMELINE_UNDERLINE_TEXT_CONFIG } from '@components/machines/components/timeline/constants';
import TimelineShimmer from './TimelineShimmer';
import { ColorFunction } from '@styles/colors/types';
import { Events, TimelineDataWithLoadingState } from '@interfaces/timeline';
import { ProductionBatch } from '@interfaces/search';
import { calculateTimelineBatches, getTimelineElementBorderRadius, getTimelineFontStyles, groupConsecutiveJobElements } from '@components/machines/components/timeline/helpers';

export default (props: {
  C: ColorFunction;
  batches: TimelineDataWithLoadingState<ProductionBatch[]>;
  blockWidth: number;
  startTime: MomentInput;
  rules: LegendRules;
  events: Events;
}) => {
  const { C, batches, blockWidth, startTime, rules, events } = props;

  const { backgroundColor: timelineBg, borderRadius: timelineBorderRadius, height: timelineLineHeight } = TIMELINE_LINE_CONFIG;

  const { fontStylesWeb } = getTimelineFontStyles();

  const containerHeight = timelineLineHeight + TIMELINE_UNDERLINE_TEXT_CONFIG.height * JOBS_CONFIG.underlineTextAmount;

  // Early return if data not loaded
  if (!batches?.loaded || !events?.loaded) {
    return (
      <div>
        <TimelineBlockTitle containerStyle={{ marginTop: TIMELINE_CONFIG.gapBetweenBlocks }} C={C}>
          {i18n.t('Jobs')}
        </TimelineBlockTitle>
        <div style={{ height: containerHeight }}>
          <TimelineShimmer style={{ height: timelineLineHeight, borderRadius: timelineBorderRadius }} />
        </div>
      </div>
    );
  }

  const { batchElements } = calculateTimelineBatches({
    batches: batches.data,
    secondWidth: rules.secondWidth,
    timelineStart: startTime,
    batchStartField: 'startedOn',
    batchEndField: 'endedOn',
    groupedByBatchEvents: events.groupedByBatch,
  });

  const groupedElements = React.useMemo(() => groupConsecutiveJobElements(batchElements), [batchElements]);

  const underlineTextStyles = {
    ...fontStylesWeb,
    color: C(TIMELINE_UNDERLINE_TEXT_CONFIG.color),
    margin: 0,
    height: TIMELINE_UNDERLINE_TEXT_CONFIG.height,
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div>
      <TimelineBlockTitle containerStyle={{ marginTop: TIMELINE_CONFIG.gapBetweenBlocks }} C={C}>
        {i18n.t('Jobs')}
      </TimelineBlockTitle>

      <div
        style={{
          height: containerHeight,
          width: blockWidth,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            borderRadius: timelineBorderRadius,
            backgroundColor: C(timelineBg),
            height: timelineLineHeight,
            display: 'flex',
            flexDirection: 'row',
          }}
        />

        {groupedElements.map((group, groupIndex) => {
          const { elements, left, width, links, shouldDisplayText } = group;

          const underlineLinksAmount = Math.min(links.length, JOBS_CONFIG.underlineTextAmount - 1);
          const linksToDisplayLeft = Math.max(links.length - underlineLinksAmount, 0);

          const borderRadiusStyles = getTimelineElementBorderRadius({
            elementLeft: left,
            elementWidth: width,
            blockWidth,
            borderRadius: timelineBorderRadius,
          });

          return (
            <React.Fragment key={`group-${groupIndex}`}>
              {/* Render all individual batch elements */}
              {elements.map((element, elementIndex) => {
                const { b } = element;

                const elementBorderRadiusStyles = getTimelineElementBorderRadius({
                  elementLeft: left,
                  elementWidth: width,
                  blockWidth,
                  borderRadius: timelineBorderRadius,
                });

                return (
                  <div
                    key={`${b?.startedOn}-job-batch-${groupIndex}-${elementIndex}`}
                    style={{
                      ...elementBorderRadiusStyles,
                      left,
                      width,
                      height: timelineLineHeight,
                      position: 'absolute',
                      top: 0,
                      backgroundColor: b?.productionBatchId == null ? 'transparent' : C('Status/Light/Productive'),
                    }}
                  />
                );
              })}

              {/* Render text once for the entire group */}
              {shouldDisplayText && (
                <div
                  style={{
                    ...borderRadiusStyles,
                    left: left,
                    width: width,
                    position: 'absolute',
                    top: timelineLineHeight,
                    overflow: 'hidden',
                  }}
                >
                  {links.slice(0, underlineLinksAmount).map((l: ProductionBatch['links'][number]) => (
                    <p key={`${l.productionOrderNumber}-${l.productionOperationNumber}`} style={underlineTextStyles}>
                      {l.productionOrderNumber}-{l.productionOperationNumber}
                    </p>
                  ))}
                  {linksToDisplayLeft > 0 && (
                    <p style={underlineTextStyles}>{i18n.t('{{jobsAmount}} more jobs', { jobsAmount: linksToDisplayLeft })}</p>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
